import { useEffect, useMemo, useRef, useState } from "react";
import { DEFAULT_RESULT_EXPORT_MAX_RECORDS, isActionWritableField, type ActionBatchCreateItem, type ActionBatchRecord, type ActionFieldChange, type ActionRecord, type ResultExportFormat, type ResultExportPolicy } from "@ai-sales/contracts";
import { CheckIcon, LockIcon } from "./icons.js";
import { ResultChart, type ChartKind } from "./ResultChart.js";
import { ResultDownloadMenu } from "./ResultDownloadMenu.js";
import { toCustomerFacingText } from "./WebsiteMessageContent.js";

interface Props {
  action: ActionRecord;
  busy: boolean;
  demo?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  onRevise?: (fields: Record<string, unknown>) => void;
  onReviseBatch?: (batchEdits: Array<{ sourceIndex: number; recordId?: string; excluded: boolean }>) => void;
  onRetryFailed?: () => void;
  previewError?: string;
  onRetryPreview?: () => void;
  onSuggestion?: (message: string) => void;
  resultExport?: ResultExportPolicy;
  objectExportPolicy?: "inherit" | "allow" | "deny";
}

const operationLabels = { query: "查询", aggregate: "汇总", create: "新建", update: "更新", delete: "删除" } as const;
const objectLabels: Record<string, string> = {
  Account: "客户",
  Contact: "联系人",
  Lead: "潜在客户",
  Opportunity: "业务机会",
  Task: "跟进任务",
  Sales_Visit__c: "销售拜访",
  AI_Quote_Request__c: "报价资料",
  Case: "售后服务请求"
};
const fieldLabels: Record<string, string> = {
  Id: "记录 ID",
  Name: "名称",
  AccountNumber: "客户编号",
  AccountId: "关联客户记录 ID",
  ContactId: "关联联系人记录 ID",
  Type: "类型",
  Industry: "行业",
  BillingCity: "所在城市",
  BillingState: "省/州",
  BillingCountry: "国家/地区",
  Power_Rating_MW__c: "项目功率（MW）",
  Storage_Capacity_MWh__c: "项目容量（MWh）",
  OwnerId: "所有者记录 ID",
  CreatedDate: "创建时间",
  LastModifiedDate: "最后更新时间",
  StageName: "业务机会阶段",
  Amount: "业务机会金额",
  Probability: "成交概率",
  CloseDate: "预计关闭日期",
  Expected_Tender_Window__c: "预计采购窗口",
  Project_Risk__c: "项目风险",
  Description: "描述",
  Phone: "联系电话",
  Email: "电子邮箱",
  Country: "国家/地区",
  FirstName: "名",
  LastName: "联系人姓名",
  Company: "公司名称",
  LeadSource: "线索来源",
  Project_Name__c: "项目名称",
  Project_Location__c: "项目地点",
  Project_Power__c: "项目功率(kW)",
  Energy_storage_capacity_AC_side_kWh__c: "项目容量(kWh)",
  Application_Scenario__c: "应用场景",
  Expected_delivery_time__c: "预计交付日期",
  Visit_Time__c: "拜访时间",
  Visit_Channel__c: "拜访方式",
  Visit_Type__c: "拜访类型",
  Topic__c: "拜访主题",
  Customer_Request__c: "客户诉求",
  Client_Name__c: "项目业主",
  Payment_Terms__c: "付款条件",
  Quotation_Type__c: "报价类型",
  Quote_Status__c: "报价状态",
  Currency__c: "币种",
  Trade_Terms__c: "贸易条款",
  Quotation_Deadline__c: "报价截止日期",
  Expected_Delivery_Date__c: "预计交付日期",
  Scope_of_supply__c: "供货范围",
  Missing_Info__c: "待补充信息",
  Quote_Summary__c: "报价摘要",
  Status: "状态",
  Subject: "主题",
  ActivityDate: "到期日期",
  Priority: "优先级",
  Visit_Summary__c: "拜访摘要",
  Next_Action__c: "下一步行动",
  SuppliedName: "联系人",
  SuppliedCompany: "公司名称",
  SuppliedPhone: "联系电话",
  SuppliedEmail: "电子邮箱",
  Reason: "故障原因",
  Opportunity__c: "关联业务机会",
  Account__c: "关联客户",
  Contact__c: "关联联系人",
  Lead__c: "关联潜在客户",
  Opportunity: "关联业务机会",
  Account: "关联客户",
  Contact: "关联联系人",
  WhoId: "关联联系人",
  WhatId: "关联业务记录"
};

const objectFieldLabels: Record<string, Record<string, string>> = {
  Case: {
    Subject: "问题主题",
    Description: "问题描述",
    Status: "处理状态",
    Reason: "问题原因",
    Type: "问题类别"
  },
  Lead: {
    Description: "需求描述",
    Status: "跟进状态"
  },
  Opportunity: {
    Description: "业务机会描述"
  },
  Task: {
    Subject: "任务主题",
    Description: "任务说明"
  }
};

function fieldLabelFor(objectApiName: string, fieldApiName: string, fallback?: string): string {
  return objectFieldLabels[objectApiName]?.[fieldApiName]
    ?? fieldLabels[fieldApiName]
    ?? fallback
    ?? fieldApiName;
}

const visitRelationshipFields = new Set(["Opportunity__c", "Account__c", "Contact__c", "Lead__c"]);
const relationshipReferenceFields = new Set([...visitRelationshipFields, "WhoId", "WhatId"]);

const picklistOptions: Record<string, Array<{ value: string; label: string }>> = {
  Status: [
    { value: "Open - Not Contacted", label: "未联系" },
    { value: "Working - Contacted", label: "已联系" },
    { value: "Not Started", label: "未开始" },
    { value: "In Progress", label: "进行中" },
    { value: "Completed", label: "已完成" },
    { value: "Waiting on someone else", label: "等待他人" }
  ],
  Priority: [
    { value: "Low", label: "低" },
    { value: "Normal", label: "普通" },
    { value: "Medium", label: "中" },
    { value: "High", label: "高" }
  ],
  StageName: [
    { value: "Prospecting", label: "初步接洽" },
    { value: "Qualification", label: "资格确认" },
    { value: "Needs Analysis", label: "需求分析" },
    { value: "Value Proposition", label: "价值建议" },
    { value: "Proposal/Price Quote", label: "方案/报价" },
    { value: "Negotiation/Review", label: "谈判/评审" },
    { value: "Closed Won", label: "赢单" },
    { value: "Closed Lost", label: "丢单" }
  ],
  Expected_Tender_Window__c: ["Q1", "Q2", "Q3", "Q4"].map((value) => ({ value, label: value })),
  Project_Risk__c: [
    { value: "Low", label: "低" },
    { value: "Medium", label: "中" },
    { value: "High", label: "高" }
  ],
  Visit_Channel__c: [
    { value: "Onsite", label: "现场拜访" },
    { value: "Phone", label: "电话" },
    { value: "Online Meeting", label: "线上会议" },
    { value: "Email", label: "邮件" }
  ],
  Visit_Type__c: [
    { value: "Requirement Discussion", label: "需求沟通" },
    { value: "Technical Meeting", label: "技术交流" },
    { value: "Quotation Discussion", label: "报价沟通" },
    { value: "Project Follow-up", label: "项目跟进" }
  ],
  Quote_Status__c: [
    { value: "Draft", label: "草稿" },
    { value: "Needs Supplement", label: "待补充" },
    { value: "Pending Confirmation", label: "待确认" },
    { value: "Submitted", label: "已提交" },
    { value: "Closed", label: "已关闭" }
  ],
  Quotation_Type__c: ["初询报价", "投标报价", "预算报价", "正式报价", "待确认"].map((value) => ({ value, label: value })),
  Currency__c: ["CNY", "USD", "EUR", "AED"].map((value) => ({ value, label: value })),
  Trade_Terms__c: [
    { value: "EXW", label: "EXW" },
    { value: "FOB", label: "FOB" },
    { value: "CIF", label: "CIF" },
    { value: "DDP", label: "DDP" },
    { value: "Other", label: "其他" }
  ],
  Application_Scenario__c: ["国内大储", "国内工商储", "用户侧储能", "独立储能电站", "新能源配储", "电网侧调频", "微网/园区", "待确认"].map((value) => ({ value, label: value })),
  LeadSource: [{ value: "Other", label: "其他渠道" }, { value: "Web", label: "官网咨询" }],
  Reason: [
    { value: "Installation", label: "安装/调试" },
    { value: "Equipment Complexity", label: "设备复杂度" },
    { value: "Performance", label: "性能问题" },
    { value: "Breakdown", label: "设备故障" },
    { value: "Equipment Design", label: "设备设计" },
    { value: "Feedback", label: "意见反馈" },
    { value: "Other", label: "其他" }
  ],
  Type: [
    { value: "Mechanical", label: "机械" },
    { value: "Electrical", label: "电气" },
    { value: "Electronic", label: "电子/通讯" },
    { value: "Structural", label: "结构" },
    { value: "Other", label: "其他" }
  ]
};

const objectPicklistOptions: Record<string, Record<string, Array<{ value: string; label: string }>>> = {
  Case: {
    Status: [
      { value: "New", label: "新建" },
      { value: "Working", label: "处理中" },
      { value: "Escalated", label: "已升级" },
      { value: "Closed", label: "已关闭" }
    ],
    Origin: [
      { value: "Phone", label: "电话" },
      { value: "Email", label: "邮件" },
      { value: "Web", label: "官网" }
    ]
  }
};

function displayValue(value: unknown, fieldApiName?: string): string {
  if (value === null || value === undefined || value === "") return "待补充";
  if (fieldApiName === "Visit_Time__c") {
    const localDateTime = toLocalDateTimeInput(value);
    if (localDateTime) return localDateTime.replace("T", " ");
  }
  const option = fieldApiName ? picklistOptions[fieldApiName]?.find((candidate) => candidate.value === String(value)) : undefined;
  if (option) return option.label;
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function toLocalDateTimeInput(value: unknown): string {
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return "";
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}

function actionTitle(action: ActionRecord): string {
  return `${operationLabels[action.plan.operation]}${objectLabels[action.plan.objectApiName] ?? action.plan.objectApiName}`;
}

function cancelButtonLabel(action: ActionRecord, website: boolean): string {
  if (website) return "取消提交";
  if (action.plan.operation === "create") return "取消新建";
  if (action.plan.operation === "update") return "取消更新";
  if (action.plan.operation === "delete") return "取消删除";
  return "取消操作";
}

function cancellationTitle(action: ActionRecord, website: boolean): string {
  if (website) return "已取消本次提交";
  if (action.plan.operation === "create") return "已取消本次新建";
  if (action.plan.operation === "update") return "已取消本次更新";
  if (action.plan.operation === "delete") return "已取消本次删除";
  return "已取消本次操作";
}

function trustedSalesforceOrigin(): string | undefined {
  if (typeof window === "undefined") return undefined;
  const parentOrigin = new URLSearchParams(window.location.search).get("parentOrigin");
  if (!parentOrigin) return undefined;
  try {
    const origin = new URL(parentOrigin);
    const trustedHost = origin.hostname.endsWith(".lightning.force.com") ||
      origin.hostname.endsWith(".salesforce.com") ||
      origin.hostname.endsWith(".sfcrmapps.cn") ||
      origin.hostname.endsWith(".sfcrmproducts.cn");
    return origin.protocol === "https:" && trustedHost ? origin.origin : undefined;
  } catch {
    return undefined;
  }
}

function salesforceRecordUrlFor(action: ActionRecord, recordId: unknown): string | undefined {
  if (action.state !== "succeeded" || typeof recordId !== "string" ||
    !/^[A-Za-z0-9]{15}(?:[A-Za-z0-9]{3})?$/.test(recordId)) return undefined;
  if (!/^[A-Za-z][A-Za-z0-9_]*$/.test(action.plan.objectApiName)) return undefined;
  const origin = trustedSalesforceOrigin();
  return origin
    ? new URL(`/lightning/r/${encodeURIComponent(action.plan.objectApiName)}/${recordId}/view`, origin).toString()
    : undefined;
}

function salesforceRecordUrl(action: ActionRecord, resultObject?: Record<string, unknown>): string | undefined {
  return action.plan.operation === "create"
    ? salesforceRecordUrlFor(action, resultObject?.recordId)
    : undefined;
}

type ResultRow = Record<string, unknown>;
type ResultColumn = { key: string; label: string };

function resultRows(value: unknown): ResultRow[] {
  return Array.isArray(value)
    ? value.filter((row): row is ResultRow => Boolean(row) && typeof row === "object" && !Array.isArray(row))
    : [];
}

function resultValue(value: unknown, fieldApiName?: string): string {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const relationship = value as Record<string, unknown>;
    const preferred = relationship.Name ?? relationship.Subject ?? relationship.Title;
    if (preferred !== undefined) return displayValue(preferred, fieldApiName);
    const scalars = Object.entries(relationship)
      .filter(([key, item]) => key !== "attributes" && (typeof item !== "object" || item === null))
      .map(([, item]) => displayValue(item));
    return scalars.length ? scalars.join(" · ") : "—";
  }
  if (Array.isArray(value)) return value.map((item) => resultValue(item, fieldApiName)).join("、") || "—";
  if (typeof value === "boolean") return value ? "是" : "否";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z$/.test(value)) {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) return new Intl.DateTimeFormat("zh-CN", {
      year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false
    }).format(date);
  }
  if (fieldApiName === "Application_Scenario__c") {
    return ({ "C&I": "工商业", "Grid-side": "源网侧", "Peak Shaving": "削峰填谷" } as Record<string, string>)[String(value)] ?? displayValue(value, fieldApiName);
  }
  return displayValue(value, fieldApiName);
}

function exportValue(value: unknown, fieldApiName?: string): unknown {
  return typeof value === "number" || typeof value === "boolean"
    ? value
    : resultValue(value, fieldApiName);
}

function resultColumns(action: ActionRecord, rows: ResultRow[], override?: ResultColumn[]): ResultColumn[] {
  if (override?.length) return override;
  const available = new Set(rows.flatMap((row) => Object.keys(row)));
  const planned = action.plan.selectFields.filter((key) => available.has(key));
  const discovered = rows.flatMap((row) => Object.keys(row));
  const ordered = [...new Set([...planned, ...discovered])]
    .filter((key) => key !== "attributes" && key !== "Id");
  const keys = ordered.length ? ordered : available.has("Id") ? ["Id"] : [];
  return keys.map((key) => ({ key, label: fieldLabelFor(action.plan.objectApiName, key) }));
}

function emptyResultSuggestions(objectApiName: string): string[] {
  const objectLabel = objectLabels[objectApiName] ?? "记录";
  return [
    `查询我能看到的全部${objectLabel}`,
    `按所有者统计我能看到的${objectLabel}`,
    `查询最近 90 天更新的${objectLabel}`
  ];
}

function EmptyReadResult({
  action,
  onSuggestion
}: {
  action: ActionRecord;
  onSuggestion?: (message: string) => void;
}) {
  const objectLabel = objectLabels[action.plan.objectApiName] ?? "记录";
  const suggestions = emptyResultSuggestions(action.plan.objectApiName);
  return <section className="query-empty-result" aria-label={`没有匹配的${objectLabel}`}>
    <div className="query-empty-result__explanation">
      <strong>这不代表 Org 中没有{objectLabel}</strong>
      <p>本次已按当前 Salesforce 用户权限和查询条件完成只读查询，结果为 0。可能是记录不归当前用户所有、共享范围不可见，或筛选条件过窄。</p>
    </div>
    <div className="query-empty-result__scope" aria-label="本次查询范围">
      <span>当前用户</span>
      <span>当前权限</span>
      {action.plan.filters.length ? <span>{action.plan.filters.length} 个筛选条件</span> : <span>未附加筛选条件</span>}
    </div>
    <div className="query-empty-result__next">
      <strong>可以继续尝试</strong>
      <div>{suggestions.map((suggestion) => onSuggestion
        ? <button type="button" key={suggestion} onClick={() => onSuggestion(suggestion)}>{suggestion}</button>
        : <span key={suggestion}>{suggestion}</span>)}</div>
    </div>
    <small>本次没有创建、更新或删除任何记录。</small>
  </section>;
}

function readResultIsEmpty(action: ActionRecord, rows: ResultRow[]): boolean {
  if (action.plan.operation === "query" || action.plan.groupBy?.length) return rows.length === 0;
  if (!rows.length) return true;
  const metrics = action.plan.metrics ?? [];
  return metrics.length === 1 && metrics[0]?.function === "count" && Number(rows[0]?.[metrics[0].alias]) === 0;
}

function queryScopeLabel(action: ActionRecord): string {
  const scope = action.plan.queryScope ?? (action.plan.recordId || action.plan.filters.some(({ field }) => field === "Id")
    ? "current_record"
    : action.plan.filters.some(({ field }) => field === "OwnerId")
      ? "current_user_owned"
      : action.plan.filters.length || action.plan.dateRange
        ? "explicit_filters"
        : "current_user_access");
  return ({
    current_user_access: "范围：全部可见记录",
    current_user_owned: "范围：当前用户负责",
    current_record: "范围：当前记录",
    explicit_filters: "范围：明确筛选条件"
  } as const)[scope];
}

function capabilitySourceLabel(action: ActionRecord): string {
  return action.plan.skillKey === "system-base-readonly" || action.plan.skillKey.startsWith("sales-")
    ? "能力：系统通用"
    : "能力：客户场景";
}

function QueryResultView({
  action,
  rows,
  totalSize,
  columns: columnOverride,
  onSuggestion,
  exportPolicy,
  objectExportPolicy = "inherit",
  resultKind = "detail"
}: {
  action: ActionRecord;
  rows: ResultRow[];
  totalSize?: number;
  columns?: ResultColumn[];
  onSuggestion?: (message: string) => void;
  exportPolicy?: ResultExportPolicy;
  objectExportPolicy?: "inherit" | "allow" | "deny";
  resultKind?: "detail" | "grouped";
}) {
  const columns = useMemo(() => resultColumns(action, rows, columnOverride), [action, columnOverride, rows]);
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const visibleRows = rows.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const effectiveTotal = typeof totalSize === "number" && totalSize >= rows.length ? totalSize : rows.length;
  const title = `${objectLabels[action.plan.objectApiName] ?? action.plan.objectApiName}${action.plan.operation === "aggregate" ? "统计" : "查询"}`;
  const exportRows = useMemo(() => rows.map((row) => Object.fromEntries(
    columns.map(({ key }) => [key, exportValue(row[key], key)])
  )), [columns, rows]);
  const exportEnabled = objectExportPolicy !== "deny" && (exportPolicy?.enabled ?? true) && rows.length <= (exportPolicy?.maxRecords ?? DEFAULT_RESULT_EXPORT_MAX_RECORDS);
  const allowedFormats = resultKind === "grouped" ? exportPolicy?.groupedFormats : exportPolicy?.detailFormats;

  useEffect(() => setPage(1), [action.contentHash, pageSize, rows.length]);

  if (!rows.length) return <EmptyReadResult action={action} onSuggestion={onSuggestion} />;

  return (
    <section className="query-result" aria-label={`${objectLabels[action.plan.objectApiName] ?? "记录"}查询结果`}>
      <div className="query-result__toolbar">
        <div className="query-result__badges">
          <span>只读查询</span>
          <span>对象：{objectLabels[action.plan.objectApiName] ?? action.plan.objectApiName}</span>
          <span>当前用户权限</span>
          <span>{queryScopeLabel(action)}</span>
          <span>筛选：{action.plan.filters.length + (action.plan.dateRange ? 1 : 0)} 项</span>
          <span>{capabilitySourceLabel(action)}</span>
          <strong>{effectiveTotal > rows.length ? `符合条件 ${effectiveTotal} 条 · 本次返回 ${rows.length} 条` : `返回 ${rows.length} 条`}</strong>
        </div>
        <div className="query-result__actions">
          {rows.length > 10 ? <label>每页
            <select value={pageSize} onChange={(event) => setPageSize(Number(event.target.value))}>
              {[10, 20, 50].map((size) => <option key={size} value={size}>{size} 条</option>)}
            </select>
          </label> : null}
          {exportEnabled ? <ResultDownloadMenu
            actionId={action.id}
            objectApiName={action.plan.objectApiName}
            resultKind={resultKind}
            title={title}
            columns={columns}
            rows={exportRows}
            allowedFormats={allowedFormats as ResultExportFormat[] | undefined}
          /> : null}
        </div>
      </div>
      <div className="query-result__table-wrap" tabIndex={0} aria-label="查询结果表格，可横向滚动">
        <table>
          <caption className="visually-hidden">{objectLabels[action.plan.objectApiName] ?? action.plan.objectApiName}查询结果</caption>
          <thead><tr><th scope="col">序号</th>{columns.map((column) => <th scope="col" key={column.key}>{column.label}</th>)}</tr></thead>
          <tbody>{visibleRows.map((row, rowIndex) => {
            const recordUrl = salesforceRecordUrlFor(action, row.Id);
            const absoluteIndex = (currentPage - 1) * pageSize + rowIndex + 1;
            const linkColumn = columns.find(({ key }) => ["Name", "Subject", "Title"].includes(key))?.key ?? columns[0]?.key;
            return <tr key={typeof row.Id === "string" ? row.Id : absoluteIndex}>
              <td data-label="序号">{absoluteIndex}</td>
              {columns.map((column) => <td data-label={column.label} key={column.key}>
                {recordUrl && column.key === linkColumn
                  ? <a href={recordUrl} target="_blank" rel="noopener noreferrer">{resultValue(row[column.key], column.key)} <span aria-hidden="true">↗</span></a>
                  : resultValue(row[column.key], column.key)}
              </td>)}
            </tr>;
          })}</tbody>
        </table>
      </div>
      {pageCount > 1 ? <nav className="query-result__pagination" aria-label="查询结果分页">
        <button type="button" disabled={currentPage === 1} onClick={() => setPage((value) => Math.max(1, value - 1))}>上一页</button>
        <span>第 {currentPage}/{pageCount} 页</span>
        <button type="button" disabled={currentPage === pageCount} onClick={() => setPage((value) => Math.min(pageCount, value + 1))}>下一页</button>
      </nav> : null}
      <p className="query-result__permission-note">仅显示当前 Salesforce 用户有权读取的字段与记录。</p>
    </section>
  );
}

function AggregateResultView({ action, rows, onSuggestion, exportPolicy, objectExportPolicy }: {
  action: ActionRecord;
  rows: ResultRow[];
  onSuggestion?: (message: string) => void;
  exportPolicy?: ResultExportPolicy;
  objectExportPolicy?: "inherit" | "allow" | "deny";
}) {
  const columns: ResultColumn[] = [
    ...(action.plan.groupBy ?? []).map((key) => ({ key, label: fieldLabelFor(action.plan.objectApiName, key) })),
    ...(action.plan.metrics ?? []).map((metric) => ({ key: metric.alias, label: metric.label }))
  ];
  const groupKey = action.plan.groupBy?.[0];
  const availableMetrics = (action.plan.metrics ?? []).filter(({ alias }) => rows.some((row) => Object.prototype.hasOwnProperty.call(row, alias)));
  const preferredView = action.plan.presentation?.preferredView ?? "auto";
  const inferredChart: ChartKind = preferredView === "funnel" || preferredView === "bar" || preferredView === "line" || preferredView === "donut"
    ? preferredView
    : groupKey === "StageName" ? "funnel" : "bar";
  const [displayMode, setDisplayMode] = useState<"chart" | "table">(preferredView === "table" ? "table" : "chart");
  const [chartKind, setChartKind] = useState<ChartKind>(inferredChart);
  const [selectedMetric, setSelectedMetric] = useState(availableMetrics[0]?.alias ?? "");
  const chartRef = useRef<SVGSVGElement>(null);
  const exportEnabled = objectExportPolicy !== "deny" && (exportPolicy?.enabled ?? true) && rows.length <= (exportPolicy?.maxRecords ?? DEFAULT_RESULT_EXPORT_MAX_RECORDS);

  useEffect(() => {
    setDisplayMode(action.plan.presentation?.preferredView === "table" ? "table" : "chart");
    setChartKind(inferredChart);
    setSelectedMetric(availableMetrics[0]?.alias ?? "");
  }, [action.contentHash, action.plan.presentation?.preferredView, availableMetrics[0]?.alias, inferredChart]);

  if (readResultIsEmpty(action, rows)) return <EmptyReadResult action={action} onSuggestion={onSuggestion} />;

  if (groupKey && availableMetrics.length) {
    const metric = availableMetrics.find(({ alias }) => alias === selectedMetric) ?? availableMetrics[0]!;
    const title = action.plan.summary || `${objectLabels[action.plan.objectApiName] ?? action.plan.objectApiName}统计`;
    const chartData = rows.map((row) => ({
      label: resultValue(row[groupKey], groupKey),
      value: Number(row[metric.alias] ?? 0)
    })).filter(({ value }) => Number.isFinite(value));
    const exportRows = rows.map((row) => Object.fromEntries(columns.map(({ key }) => [key, exportValue(row[key], key)])));
    return <section className="analytics-result" aria-label={`${title}分析结果`}>
      <div className="analytics-result__toolbar">
        <div className="analytics-result__view-switch" role="group" aria-label="结果展示方式">
          <button type="button" aria-pressed={displayMode === "chart"} onClick={() => setDisplayMode("chart")}>图表</button>
          <button type="button" aria-pressed={displayMode === "table"} onClick={() => setDisplayMode("table")}>表格</button>
        </div>
        {displayMode === "chart" ? <div className="analytics-result__controls">
          {availableMetrics.length > 1 ? <label>指标
            <select value={metric.alias} onChange={(event) => setSelectedMetric(event.target.value)}>
              {availableMetrics.map((item) => <option key={item.alias} value={item.alias}>{item.label}</option>)}
            </select>
          </label> : null}
          <label>图形
            <select value={chartKind} onChange={(event) => setChartKind(event.target.value as ChartKind)}>
              <option value="funnel">漏斗图</option>
              <option value="bar">柱状图</option>
              <option value="line">折线图</option>
              <option value="donut">环形图</option>
            </select>
          </label>
          {exportEnabled ? <ResultDownloadMenu
            actionId={action.id}
            objectApiName={action.plan.objectApiName}
            resultKind="chart"
            title={title}
            columns={columns}
            rows={exportRows}
            chartRef={chartRef}
            allowedFormats={exportPolicy?.chartFormats}
          /> : null}
        </div> : null}
      </div>
      {displayMode === "chart"
        ? <div className="analytics-result__chart-wrap"><ResultChart ref={chartRef} kind={chartKind} title={title} groupLabel={fieldLabelFor(action.plan.objectApiName, groupKey)} metricLabel={metric.label} data={chartData} /></div>
        : <QueryResultView action={action} rows={rows} columns={columns} resultKind="grouped" exportPolicy={exportPolicy} objectExportPolicy={objectExportPolicy} />}
      <p className="query-result__permission-note">图表与下载仅使用本次当前 Salesforce 用户权限下返回的数据。</p>
    </section>;
  }
  const row = rows[0] ?? {};
  const metrics = availableMetrics.filter(({ alias }) => Object.prototype.hasOwnProperty.call(row, alias));
  return metrics.length ? <section className="query-metric-result">
    <div className="query-metric-grid" aria-label="统计结果">
      {metrics.map((metric) => <div key={metric.alias}><strong>{resultValue(row[metric.alias], metric.alias)}</strong><span>{metric.label}</span><small>统计口径：当前 Salesforce 用户可见记录</small></div>)}
    </div>
  </section> : null;
}

function FieldEditor({
  change,
  objectApiName,
  value,
  onChange
}: {
  change: ActionFieldChange;
  objectApiName: string;
  value: unknown;
  onChange: (value: string) => void;
}) {
  const isLongText = change.fieldApiName === "Description" || change.fieldApiName.endsWith("Summary__c") || change.fieldApiName === "Missing_Info__c" || change.fieldApiName === "Scope_of_supply__c";
  const options = objectPicklistOptions[objectApiName]?.[change.fieldApiName] ?? picklistOptions[change.fieldApiName];
  const isDateTime = change.fieldApiName === "Visit_Time__c";
  const isDate = change.fieldApiName === "CloseDate" || change.fieldApiName === "ActivityDate" || change.fieldApiName === "Expected_delivery_time__c" || change.fieldApiName === "Quotation_Deadline__c" || change.fieldApiName.endsWith("_Date__c");
  const dateTimeValue = isDateTime && value ? toLocalDateTimeInput(value) : "";
  return (
    <div className="field-diff-row">
      <label htmlFor={`field-${change.fieldApiName}`}>{fieldLabelFor(objectApiName, change.fieldApiName, change.label)}</label>
      <div className="field-diff-row__values">
        <span className="field-diff-row__old">{displayValue(change.oldValue, change.fieldApiName)}</span>
        <span className="field-diff-row__arrow" aria-hidden="true">→</span>
        {options ? (
          <select id={`field-${change.fieldApiName}`} value={String(value ?? "")} onChange={(event) => onChange(event.target.value)}>
            {!options.some((option) => option.value === String(value ?? "")) ? <option value={String(value ?? "")}>{displayValue(value, change.fieldApiName)}</option> : null}
            {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        ) : isDateTime ? (
          <input id={`field-${change.fieldApiName}`} type="datetime-local" value={dateTimeValue} onChange={(event) => onChange(new Date(event.target.value).toISOString())} />
        ) : isDate ? (
          <input id={`field-${change.fieldApiName}`} type="date" value={String(value ?? "").slice(0, 10)} onChange={(event) => onChange(event.target.value)} />
        ) : isLongText ? (
          <textarea id={`field-${change.fieldApiName}`} value={displayValue(value)} onChange={(event) => onChange(event.target.value)} rows={2} />
        ) : (
          <input id={`field-${change.fieldApiName}`} value={displayValue(value)} onChange={(event) => onChange(event.target.value)} />
        )}
      </div>
    </div>
  );
}

function ExecutionResult({ action, website = false, onSuggestion, resultExport, objectExportPolicy }: {
  action: ActionRecord;
  website?: boolean;
  onSuggestion?: (message: string) => void;
  resultExport?: ResultExportPolicy;
  objectExportPolicy?: "inherit" | "allow" | "deny";
}) {
  const succeeded = action.state === "succeeded";
  const isRead = action.plan.operation === "query" || action.plan.operation === "aggregate";
  const batchCount = action.plan.batchRecords?.length
    ?? action.plan.batchCreate?.items.filter(({ excluded }) => !excluded).length
    ?? 0;
  const resultFields = batchCount ? [] : action.plan.fieldChanges.length
    ? action.plan.fieldChanges
    : Object.entries(action.plan.fields).map(([fieldApiName, newValue]) => ({
      fieldApiName,
      label: fieldLabelFor(action.plan.objectApiName, fieldApiName),
      newValue
    }));
  const fields = resultFields.filter(({ fieldApiName }) =>
    isActionWritableField(action.plan.objectApiName, fieldApiName) && !relationshipReferenceFields.has(fieldApiName)
  );
  const resultObject = action.result && typeof action.result === "object" ? action.result as Record<string, unknown> : undefined;
  const recordUrl = website ? undefined : salesforceRecordUrl(action, resultObject);
  const rawResultRows = action.plan.operation === "aggregate" ? resultObject?.metrics : resultObject?.records;
  const structuredRows = resultRows(rawResultRows);
  const emptyReadResult = succeeded && isRead && Array.isArray(rawResultRows) && readResultIsEmpty(action, structuredRows);
  const resultCount = Array.isArray(rawResultRows) ? structuredRows.length : undefined;
  const totalSize = typeof resultObject?.totalSize === "number" ? resultObject.totalSize : undefined;
  const omittedFields = Array.isArray(resultObject?.omittedFields)
    ? resultObject.omittedFields.filter((field): field is string => typeof field === "string")
    : [];
  const aggregateSummary = action.plan.operation === "aggregate"
    ? !Array.isArray(rawResultRows)
      ? "统计结果已从 Salesforce 返回"
      : structuredRows.length === 0
        ? "当前条件下没有统计结果"
        : action.plan.groupBy?.length
          ? `已返回 ${structuredRows.length} 组统计结果`
          : (() => {
              const row = structuredRows[0] ?? {};
              const values = (action.plan.metrics ?? []).flatMap((metric) =>
                Object.prototype.hasOwnProperty.call(row, metric.alias)
                  ? [`${metric.label}：${displayValue(row[metric.alias])}`]
                  : []
              );
              return values.length ? values.join(" · ") : "统计结果已从 Salesforce 返回";
            })()
    : undefined;
  const failureMessage = isRead
    ? "本次查询暂未完成，请重新发送查询指令；系统没有修改任何记录。"
    : "本次操作暂未完成，请核对目标记录和修改内容后重试；系统没有修改任何记录。";
  const isWebsiteComplaint = Boolean(website && action.plan.objectApiName === "Case" &&
    /投诉|升级处理/.test(`${action.plan.summary} ${String(action.plan.fields.Subject ?? "")}`));
  const successTitle = website && action.plan.operation === "create" && action.plan.objectApiName === "Lead"
    ? "项目咨询已提交"
    : website && action.plan.operation === "create" && action.plan.objectApiName === "Case"
      ? isWebsiteComplaint ? "投诉反馈已提交" : "服务申请已提交"
    : isRead
    ? `${objectLabels[action.plan.objectApiName] ?? "记录"}${action.plan.operation === "aggregate" ? "统计完成" : "查询完成"}`
    : `${objectLabels[action.plan.objectApiName] ?? "记录"}已${operationLabels[action.plan.operation]}`;
  return (
    <div className={`execution-result execution-result--${emptyReadResult ? "empty" : succeeded ? "success" : "failed"}`}>
      <div className="execution-result__title">
        <span aria-hidden="true">{emptyReadResult ? "0" : succeeded ? "✓" : "!"}</span>
        <div>
          <strong>{emptyReadResult ? `当前条件下没有匹配的${objectLabels[action.plan.objectApiName] ?? "记录"}` : succeeded ? successTitle : "操作未完成"}</strong>
          <p>{succeeded ? (website
            ? action.plan.objectApiName === "Case"
              ? isWebsiteComplaint ? "服务负责人已收到您的投诉反馈" : "技术服务团队已收到您的申请"
              : "储能顾问已收到您的项目需求"
            : emptyReadResult
              ? "只读查询已完成并返回 0 条，系统没有修改任何记录。"
            : isRead
              ? (action.plan.operation === "aggregate"
                ? aggregateSummary
                : resultCount === undefined
                  ? "结果已从 Salesforce 返回"
                  : resultCount === 0
                    ? "未找到匹配记录"
                    : resultCount === 1
                      ? "已找到 1 条匹配记录"
                    : `已返回 ${resultCount} 条记录，无需确认`)
              : batchCount
                ? `${batchCount} 条记录更新成功`
                : `${fields.length} 个字段处理成功`)
            : website
              ? "当前暂未能完成提交，请核对信息后重试；如仍无法提交，请继续留言。"
              : failureMessage}</p>
        </div>
      </div>
      {succeeded && !isRead && fields.length ? (
        <dl className="execution-result__fields">
          {fields.map((field) => (
            <div key={field.fieldApiName}>
              <dt>{fieldLabelFor(action.plan.objectApiName, field.fieldApiName, field.label)}</dt>
              <dd>{displayValue(field.newValue, field.fieldApiName)}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {succeeded && !website && action.plan.operation === "query" && Array.isArray(rawResultRows)
        ? <QueryResultView action={action} rows={structuredRows} totalSize={totalSize} onSuggestion={onSuggestion} exportPolicy={resultExport} objectExportPolicy={objectExportPolicy} />
        : null}
      {succeeded && !website && action.plan.operation === "aggregate" && Array.isArray(rawResultRows)
        ? <AggregateResultView action={action} rows={structuredRows} onSuggestion={onSuggestion} exportPolicy={resultExport} objectExportPolicy={objectExportPolicy} />
        : null}
      {succeeded && isRead && omittedFields.length ? <p className="query-result__field-notice" role="status">
        已按当前用户字段权限省略 {omittedFields.length} 个不可读取的展示字段；筛选、排序、分组和统计口径未被静默修改。
      </p> : null}
      {recordUrl ? (
        <a className="execution-result__record-link" href={recordUrl} target="_blank" rel="noopener noreferrer">
          在 Salesforce 中查看记录 <span aria-hidden="true">↗</span>
        </a>
      ) : null}
    </div>
  );
}

function sourceKey(value: unknown): string {
  return `${typeof value}:${String(value)}`;
}

function BatchUpdatePreview({ objectApiName, records, requestedValues, onToggle }: { objectApiName: string; records: ActionBatchRecord[]; requestedValues: Array<string | number | boolean>; onToggle: (recordId: string, excluded: boolean) => void }) {
  const counts = new Map<string, { value: string | number | boolean; count: number }>();
  for (const value of requestedValues) {
    const key = sourceKey(value);
    const current = counts.get(key);
    counts.set(key, { value, count: (current?.count ?? 0) + 1 });
  }
  const duplicates = [...counts.values()].filter(({ count }) => count > 1);
  const matchedKeys = new Set(records.map(({ sourceValue }) => sourceKey(sourceValue)));
  const unmatched = [...counts.values()].filter(({ value }) => !matchedKeys.has(sourceKey(value)));
  const matchedRecordCounts = new Map<string, number>();
  for (const record of records) {
    const key = sourceKey(record.sourceValue);
    matchedRecordCounts.set(key, (matchedRecordCounts.get(key) ?? 0) + 1);
  }
  return (
    <div className="batch-preview">
      <div className="batch-preview__summary">
        <strong>批量更新明细</strong>
        <span>识别 {requestedValues.length} 行输入，匹配 {records.length} 条系统记录</span>
      </div>
      {duplicates.length ? (
        <div className="batch-preview__notice" role="status">
          <strong>发现重复输入，已保留并标记：</strong>
          {duplicates.map(({ value, count }) => <span key={sourceKey(value)}>“{String(value)}”出现 {count} 次</span>)}
          <small>若重复输入对应同一条系统记录，只写入一次，避免重复修改；不会静默删除重复信息。</small>
        </div>
      ) : null}
      {unmatched.length ? (
        <div className="batch-preview__notice batch-preview__notice--warning">
          <strong>以下输入未匹配，不会执行：</strong>
          {unmatched.map(({ value }) => <span key={sourceKey(value)}>“{String(value)}”</span>)}
        </div>
      ) : null}
      <div className="batch-preview__records">
        {records.map((record) => {
          const sameNameRecords = matchedRecordCounts.get(sourceKey(record.sourceValue)) ?? 1;
          return (
            <article className={`batch-preview-record${record.excluded ? " batch-preview-record--excluded" : ""}`} key={record.recordId}>
              <header>
                <div><strong>{record.recordLabel ?? String(record.sourceValue)}</strong><small>记录 ID：{record.recordId}</small></div>
                <div className="batch-preview-record__badges">
                  {record.sourceOccurrences.length > 1 ? <span>输入第 {record.sourceOccurrences.join("、")} 行</span> : <span>输入第 {record.sourceOccurrences[0]} 行</span>}
                  {sameNameRecords > 1 ? <span>同名记录 {sameNameRecords} 条</span> : null}
                  <label className="batch-record-toggle"><input type="checkbox" checked={!record.excluded} onChange={(event) => onToggle(record.recordId, !event.target.checked)} />本次处理</label>
                </div>
              </header>
              <dl>
                {record.fieldChanges.map((change) => (
                  <div key={change.fieldApiName}>
                    <dt>{fieldLabelFor(objectApiName, change.fieldApiName, change.label)}</dt>
                    <dd><span>{displayValue(change.oldValue, change.fieldApiName)}</span><b aria-hidden="true">→</b><strong>{displayValue(change.newValue, change.fieldApiName)}</strong></dd>
                  </div>
                ))}
              </dl>
            </article>
          );
        })}
      </div>
    </div>
  );
}

function BatchCreatePreview({ objectApiName, items, onToggle }: { objectApiName: string; items: ActionBatchCreateItem[]; onToggle: (sourceIndex: number, excluded: boolean) => void }) {
  const activeItems = items.filter(({ excluded }) => !excluded);
  const duplicateLabels = new Map<string, number>();
  for (const item of activeItems) {
    const key = item.sourceLabel?.trim();
    if (key) duplicateLabels.set(key, (duplicateLabels.get(key) ?? 0) + 1);
  }
  const duplicates = [...duplicateLabels.entries()].filter(([, count]) => count > 1);
  return (
    <div className="batch-preview">
      <div className="batch-preview__summary"><strong>批量新建明细</strong><span>准备创建 {activeItems.length} 条，已排除 {items.length - activeItems.length} 条</span></div>
      {duplicates.length ? (
        <div className="batch-preview__notice" role="status">
          <strong>发现重复输入，未自动去重：</strong>
          {duplicates.map(([label, count]) => <span key={label}>“{label}”出现 {count} 次</span>)}
          <small>确认前可以继续说明要排除、修改或保留哪些来源行。</small>
        </div>
      ) : null}
      <div className="batch-preview__records">
        {items.map((item) => (
          <article className={`batch-preview-record${item.excluded ? " batch-preview-record--excluded" : ""}`} key={item.sourceIndex}>
            <header>
              <div><strong>{item.sourceLabel ?? `第 ${item.sourceIndex} 条`}</strong><small>附件或输入第 {item.sourceIndex} 行</small></div>
              <div className="batch-preview-record__badges"><span>{item.excluded ? "已排除" : item.warnings.length ? "需关注" : "可创建"}</span><label className="batch-record-toggle"><input type="checkbox" checked={!item.excluded} onChange={(event) => onToggle(item.sourceIndex, !event.target.checked)} />本次处理</label></div>
            </header>
            {item.warnings.length ? <p className="batch-preview-record__warning">{item.warnings.join("；")}</p> : null}
            <dl>
              {item.fieldChanges.map((change) => (
                <div key={change.fieldApiName}><dt>{fieldLabelFor(objectApiName, change.fieldApiName, change.label)}</dt><dd className="batch-preview-record__create-value"><strong>{displayValue(change.newValue, change.fieldApiName)}</strong></dd></div>
              ))}
            </dl>
          </article>
        ))}
      </div>
    </div>
  );
}

function batchSelection(action: ActionRecord): Record<string, boolean> {
  return Object.fromEntries([
    ...(action.plan.batchRecords ?? []).map((record) => [`record:${record.recordId}`, record.excluded] as const),
    ...(action.plan.batchCreate?.items ?? []).map((item) => [`source:${item.sourceIndex}`, item.excluded] as const)
  ]);
}

function ReadExecutionPending({ action }: { action: ActionRecord }) {
  const [phase, setPhase] = useState<"reading" | "organizing" | "waiting">("reading");
  const aggregate = action.plan.operation === "aggregate";
  const objectLabel = objectLabels[action.plan.objectApiName] ?? action.plan.objectApiName;

  useEffect(() => {
    const organizingTimer = window.setTimeout(() => setPhase("organizing"), 2_500);
    const waitingTimer = window.setTimeout(() => setPhase("waiting"), 7_500);
    return () => {
      window.clearTimeout(organizingTimer);
      window.clearTimeout(waitingTimer);
    };
  }, []);

  const status = phase === "reading"
    ? `正在从 Salesforce 读取${objectLabel}数据…`
    : phase === "organizing"
      ? aggregate ? "正在整理统计结果…" : "正在整理可见字段和记录…"
      : "数据仍在安全读取中，完成后会自动显示结果…";

  return (
    <section className="read-execution" role="status" aria-live="polite" aria-label={`${aggregate ? "统计" : "查询"}${objectLabel}进行中`}>
      <div className="read-execution__heading">
        <span className="read-execution__icon"><CheckIcon size={18} /></span>
        <div>
          <strong>{aggregate ? `正在统计${objectLabel}` : `正在查询${objectLabel}`}</strong>
          <p>{action.plan.summary}</p>
        </div>
        <span className="read-execution__activity" aria-hidden="true"><i /><i /><i /></span>
      </div>
      <div className="read-execution__meta" aria-label="查询保护范围">
        <span>当前用户权限</span>
        <span>只读查询</span>
        {action.plan.limit ? <span>最多 {action.plan.limit} 条</span> : null}
      </div>
      <div className="read-execution__body">
        <p className="read-execution__status"><span aria-hidden="true" />{status}</p>
        <div className="read-execution__skeleton" aria-hidden="true">
          <i /><i /><i /><i />
        </div>
      </div>
      <p className="read-execution__note"><LockIcon size={14} />仅按当前 Salesforce 用户权限读取，不会创建、更新或删除记录</p>
    </section>
  );
}

export function ActionProposal({ action, busy, demo = false, onConfirm, onCancel, onRevise, onReviseBatch, onRetryFailed, previewError, onRetryPreview, onSuggestion, resultExport, objectExportPolicy }: Props) {
  const remaining = action.confirmationsRequired - action.confirmationsReceived;
  const isDelete = action.plan.operation === "delete";
  const batchRecords = action.plan.batchRecords ?? [];
  const batchCreateItems = action.plan.batchCreate?.items ?? [];
  const awaitingPreview = (action.plan.operation === "update" || action.plan.operation === "delete") &&
    !action.plan.expectedLastModifiedDate && !batchRecords.length;
  const awaitingTargetMatch = awaitingPreview && !action.plan.recordId;
  const editableChanges = useMemo<ActionFieldChange[]>(() => {
    if (batchRecords.length || batchCreateItems.length) return [];
    const changes = action.plan.fieldChanges.length
      ? action.plan.fieldChanges
      : Object.entries(action.plan.fields).map(([fieldApiName, newValue]) => ({
        fieldApiName,
        label: fieldLabelFor(action.plan.objectApiName, fieldApiName),
        newValue
      }));
    return changes.filter(({ fieldApiName }) =>
      isActionWritableField(action.plan.objectApiName, fieldApiName) &&
      !(action.plan.operation === "create" && action.plan.objectApiName === "Sales_Visit__c" && visitRelationshipFields.has(fieldApiName))
    );
  }, [action.plan.fieldChanges, action.plan.fields, action.plan.objectApiName, action.plan.operation, batchRecords.length, batchCreateItems.length]);
  const [draftFields, setDraftFields] = useState<Record<string, unknown>>(action.plan.fields);
  const [batchExclusions, setBatchExclusions] = useState<Record<string, boolean>>(() => batchSelection(action));

  useEffect(() => setDraftFields(action.plan.fields), [action.contentHash, action.plan.fields]);
  useEffect(() => setBatchExclusions(batchSelection(action)), [action.contentHash]);

  const dirtyFields = useMemo(() => Object.fromEntries(
    Object.entries(draftFields).filter(([key, value]) => displayValue(value) !== displayValue(action.plan.fields[key]))
  ), [action.plan.fields, draftFields]);
  const displayedBatchRecords = batchRecords.map((record) => ({ ...record, excluded: batchExclusions[`record:${record.recordId}`] ?? record.excluded }));
  const displayedBatchCreateItems = batchCreateItems.map((item) => ({ ...item, excluded: batchExclusions[`source:${item.sourceIndex}`] ?? item.excluded }));
  const batchDirty = displayedBatchRecords.some((record, index) => record.excluded !== batchRecords[index]?.excluded) ||
    displayedBatchCreateItems.some((item, index) => item.excluded !== batchCreateItems[index]?.excluded);
  const isDirty = Object.keys(dirtyFields).length > 0 || batchDirty;
  const activeBatchCount = displayedBatchRecords.length
    ? displayedBatchRecords.filter(({ excluded }) => !excluded).length
    : displayedBatchCreateItems.filter(({ excluded }) => !excluded).length;
  const isVisitLinkedToOpportunity = action.plan.operation === "create" &&
    action.plan.objectApiName === "Sales_Visit__c" &&
    typeof action.plan.fields.Opportunity__c === "string";
  const isVisitLinkedToMultipleRecords = isVisitLinkedToOpportunity && [
    action.plan.fields.Account__c,
    action.plan.fields.Contact__c,
    action.plan.fields.Lead__c
  ].filter((value) => typeof value === "string").length > 0;
  const isWebsiteComplaint = Boolean(demo && action.plan.objectApiName === "Case" &&
    /投诉|升级处理/.test(`${action.plan.summary} ${String(action.plan.fields.Subject ?? "")}`));
  const needsAttention = action.state === "needs_attention";
  const unresolvedDependencies = action.dependencies?.filter(({ resolvedRecordId }) => !resolvedRecordId) ?? [];
  const isInteractive = action.state === "pending_confirmation" || needsAttention;

  if (["succeeded", "failed"].includes(action.state)) return <ExecutionResult action={action} website={demo} onSuggestion={busy ? undefined : onSuggestion} resultExport={resultExport} objectExportPolicy={objectExportPolicy} />;
  if (!demo && action.state === "executing" && ["query", "aggregate"].includes(action.plan.operation)) {
    return <ReadExecutionPending action={action} />;
  }
  if (action.state === "cancelled") {
    return (
      <div className="action-cancelled" role="status">
        <span aria-hidden="true">×</span>
        <div>
          <strong>{cancellationTitle(action, demo)}</strong>
          <p>{demo ? "本次信息未提交，您可以继续咨询或重新整理需求。" : "未写入或修改任何数据，您可以继续提出新的操作需求。"}</p>
        </div>
      </div>
    );
  }
  return (
    <section className={`action-proposal ${isDelete ? "action-proposal--danger" : ""}`} aria-label={demo ? "服务请求预览" : "Salesforce 操作预览"}>
      <div className="action-proposal__heading">
        <span className="action-proposal__icon"><CheckIcon size={18} /></span>
        <div>
          <strong>{demo && action.plan.objectApiName === "Lead" ? "提交项目咨询" : isWebsiteComplaint ? "提交投诉反馈" : demo && action.plan.objectApiName === "Case" ? "提交服务申请" : actionTitle(action)}</strong>
          <p>{demo ? toCustomerFacingText(action.plan.summary) : action.plan.summary}</p>
        </div>
        {action.groupId ? <span className="action-group-step">步骤 {action.groupSequence}/{action.groupSize}</span> : null}
        <span className="action-version">当前预览 v{action.version}</span>
      </div>
      {action.plan.recordLabel || action.plan.recordId ? (
        <div className="matched-record">
          <span>{demo ? "本次提交信息" : isVisitLinkedToMultipleRecords ? "关联业务记录" : isVisitLinkedToOpportunity ? "关联业务机会" : action.plan.operation === "create" ? "新建记录" : "目标记录"}</span>
          <strong>{action.plan.recordLabel ?? action.plan.recordId}</strong>
        </div>
      ) : null}
      {isDelete ? (
        <div className="action-attention" role="alert">
          <strong>永久删除一条 Salesforce 记录</strong>
          <p>系统会在执行前再次读取目标记录和最新版本，并校验当前用户删除权限。删除不能与其他动作组合，且需要两次明确确认。</p>
        </div>
      ) : null}
      {action.lastChangeSummary && action.version > 1 ? <p className="draft-update-note">已根据刚才的补充更新预览：{action.lastChangeSummary}</p> : null}
      {action.plan.symbolicBindings?.length ? <div className="symbolic-binding-list" aria-label="跨对象关联">
        <strong>执行关联</strong>
        {action.plan.symbolicBindings.map((binding) => {
          const dependency = action.dependencies?.find(({ fieldApiName }) => fieldApiName === binding.fieldApiName);
          return <p key={`${binding.fieldApiName}:${binding.sourceActionIndex}`}>
            <span>{binding.label}</span>
            <small>{dependency?.resolvedRecordId ? "已取得真实记录，等待执行" : `将在步骤 ${binding.sourceActionIndex} 成功后自动带入`}</small>
          </p>;
        })}
      </div> : null}
      {needsAttention ? (
        <div className="action-attention" role="alert">
          <strong>需要处理后继续</strong>
          <p>{action.error ?? "本次执行暂未完成，草稿和预览已保留。请修改问题项后重新确认。"}</p>
        </div>
      ) : null}
      {previewError && awaitingPreview ? (
        <div className="action-attention" role="alert">
          <strong>记录核对暂未完成</strong>
          <p>{previewError} 系统尚未执行任何写入。</p>
        </div>
      ) : awaitingPreview ? (
        <p className="action-status action-status--preparing">{demo
          ? "正在核对当前信息…"
          : awaitingTargetMatch
            ? "正在按名称和筛选条件匹配目标记录；唯一命中后将生成更新预览…"
            : "正在读取目标记录，核对字段原值和最后更新时间…"}</p>
      ) : batchRecords.length && action.plan.batchUpdate ? (
        <BatchUpdatePreview objectApiName={action.plan.objectApiName} records={displayedBatchRecords} requestedValues={action.plan.batchUpdate.requestedValues} onToggle={(recordId, excluded) => setBatchExclusions((current) => ({ ...current, [`record:${recordId}`]: excluded }))} />
      ) : batchCreateItems.length ? (
        <BatchCreatePreview objectApiName={action.plan.objectApiName} items={displayedBatchCreateItems} onToggle={(sourceIndex, excluded) => setBatchExclusions((current) => ({ ...current, [`source:${sourceIndex}`]: excluded }))} />
      ) : <div className="field-diff-list">
        {editableChanges.map((change) => (
          <FieldEditor
            key={change.fieldApiName}
            change={change}
            objectApiName={action.plan.objectApiName}
            value={draftFields[change.fieldApiName]}
            onChange={(value) => setDraftFields((current) => ({ ...current, [change.fieldApiName]: value }))}
          />
        ))}
      </div>}
      {isInteractive ? (
        <div className="action-proposal__actions">
          <button className="button button--secondary" type="button" onClick={onCancel} disabled={busy}>
            {cancelButtonLabel(action, demo)}
          </button>
          {previewError && awaitingPreview && onRetryPreview ? (
            <button className="button button--primary" type="button" onClick={onRetryPreview} disabled={busy}>
              重新核对
            </button>
          ) : !awaitingPreview && isDirty && (onRevise || onReviseBatch) ? (
            <button className="button button--secondary" type="button" onClick={() => batchDirty && onReviseBatch ? onReviseBatch([
              ...displayedBatchRecords.map((record) => ({ sourceIndex: record.sourceOccurrences[0]!, recordId: record.recordId, excluded: record.excluded })),
              ...displayedBatchCreateItems.map((item) => ({ sourceIndex: item.sourceIndex, excluded: item.excluded }))
            ]) : onRevise?.(dirtyFields)} disabled={busy || (batchDirty && activeBatchCount === 0)}>更新预览</button>
          ) : null}
          {previewError && awaitingPreview ? null : needsAttention && (batchRecords.length || batchCreateItems.length) && onRetryFailed ? (
            <button className="button button--primary" type="button" onClick={onRetryFailed} disabled={busy || isDirty || activeBatchCount === 0}>
              {busy ? "处理中…" : `只重试失败项（${activeBatchCount} 条）`}
            </button>
          ) : (
            <button className={isDelete ? "button button--danger" : "button button--primary"} type="button" onClick={onConfirm} disabled={busy || isDirty || awaitingPreview || needsAttention || unresolvedDependencies.length > 0}>
              {unresolvedDependencies.length ? "等待前置步骤" : needsAttention ? "请先处理问题项" : awaitingPreview ? "核对中…" : busy ? "处理中…" : isDelete ? `确认删除（还需 ${remaining} 次）` : demo && action.plan.operation === "create" ? "确认提交" : batchCreateItems.length ? `确认新建（${displayedBatchCreateItems.filter(({ excluded }) => !excluded).length} 条）` : action.plan.operation === "create" ? "确认新建" : batchRecords.length ? `确认更新（${displayedBatchRecords.filter(({ excluded }) => !excluded).length} 条）` : "确认更新"}
            </button>
          )}
        </div>
      ) : <p className={`action-status action-status--${action.state}`}>{action.state === "executing" ? (demo ? "正在提交，请稍候…" : "正在按最新预览执行…") : demo ? "当前申请已处理" : `状态：${action.state}`}</p>}
      <p className="permission-note"><LockIcon size={14} />{demo
        ? action.plan.objectApiName === "Case"
          ? isWebsiteComplaint
            ? "仅提交预览中的信息；可直接修改预览，确认后再提交服务负责人升级处理"
            : "仅提交预览中的信息；可直接修改预览，确认后再发送给技术服务团队"
          : "仅提交预览中的信息；可直接修改预览，确认后再安排储能顾问跟进"
        : isDelete
          ? "仅删除上方明确列出的一条记录；记录变化或权限不足时会停止执行"
          : "仅处理预览中的字段；可直接修改并更新预览，确认后再执行"}</p>
    </section>
  );
}
